import { mergeStyles } from '@cian/utils';
import * as React from 'react';

import { AbstractNavigation, IAbstractNavigationState } from './Abstract';
import styles from './Navigation.css';
import { ImageLoad } from '../slider/image_load';

export interface ISliderNavigationProps {
  slides: string[];
  currentSlide: number;
  offsetStep?: number;
  slideOffset?: number;
  imageStyle?: React.CSSProperties;
  containerStyles?: React.CSSProperties;

  onChangeSlide(index: number): void;
  onImageLoad?(): void;
}

export type ISliderNavigationState = IAbstractNavigationState;

export class Navigation extends AbstractNavigation<ISliderNavigationProps, ISliderNavigationState> {
  public containerElement: HTMLDivElement;
  public navigationElement: HTMLDivElement;
  public navigationElements: Map<number, HTMLDivElement> = new Map();

  public UNSAFE_componentWillReceiveProps(nextProps: ISliderNavigationProps) {
    if (this.props.slides === nextProps.slides) {
      super.UNSAFE_componentWillReceiveProps(nextProps);
    } else {
      this.imagesLoaded = 0;
      this.setState({
        imagesLoaded: false,
      });
    }
  }

  public componentDidUpdate(prevProps: ISliderNavigationProps, prevState: ISliderNavigationState) {
    if (this.props.slides.length !== prevProps.slides.length) {
      this.navigationElements = new Map();
    }
    if (!prevState.imagesLoaded && this.state.imagesLoaded) {
      this.updateSizes();
    }
  }

  public get transformStyle() {
    return {
      transform: `translateX(-${this.state.offsetLeft}px)`,
    };
  }

  public render() {
    return (
      <div
        {...mergeStyles(styles['container'], this.props.containerStyles)}
        ref={el => {
          if (el) {
            this.containerElement = el;
          }
        }}
      >
        <div
          {...mergeStyles('cg-row', styles['track'], this.state.swiped && styles['track--grabbing'], {
            ...this.transformStyle,
          })}
          ref={el => {
            if (el) {
              this.navigationElement = el;
            }
          }}
          onMouseDown={this.onSwipeStart}
          onMouseMove={e => this.state.swipeStart && this.onSwipeMove(e)}
          onMouseUp={this.onSwipeEnd}
          onMouseLeave={e => this.state.swiped && this.onSwipeEnd(e)}
          onTouchStart={this.onSwipeStart}
          onTouchMove={e => this.state.swipeStart && this.onSwipeMove(e)}
          onTouchEnd={this.onSwipeEnd}
        >
          {this.props.slides.map((slide, index) => (
            <div
              {...mergeStyles(styles['item'], index === this.props.currentSlide && styles['item--active'], {
                marginRight: this.props.slideOffset,
              })}
              data-react-gallery-navigation-index={index}
              key={`slide-${index}`}
              ref={el => {
                if (el) {
                  this.navigationElements.set(index, el);
                }
              }}
            >
              <ImageLoad
                imgProps={{
                  draggable: false,
                }}
                onLoad={() => {
                  this.imageOnLoad();
                  if (this.props.onImageLoad) {
                    this.props.onImageLoad();
                  }
                }}
                onError={() => this.imageOnLoad()}
                src={slide}
                style={this.props.imageStyle}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  public onSwipeEnd = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!this.state.swiped && (e.target as HTMLElement).closest('[data-react-gallery-navigation-index]') !== null) {
      const id = ((e.target as HTMLElement).closest('[data-react-gallery-navigation-index]') as HTMLElement).dataset[
        'reactGalleryNavigationIndex'
      ];
      this.onNavigationItemClick(e, Number(id));

      return;
    }
    this.setState({
      swiped: false,
      swipeStart: false,
    });
  };
}
